/* eslint-disable max-lines -- Figma-exported illustration; the length is SVG path/filter data, not logic */
import { FormattedMessage, useIntl } from 'react-intl';

import type { SVGProps } from 'react';

/**
 * Illustration for the Clinician downgrade-reconsider modal: a mock template
 * editor titled "My template" with Chat/Transcript tabs and a chat composer
 * asking for a SOAP note. Converted from the Figma export
 * (downgrade-custom-templates.svg) so the labels can be translated via
 * react-intl.
 */
// theme-agnostic: fixed-palette Figma illustration, renders identically in both themes
export const DowngradeCustomTemplatesSvg = (props: SVGProps<SVGSVGElement>) => {
  const intl = useIntl();

  return (
    // direction="ltr": the artwork is composed left-to-right; without it, an
    // inherited dir="rtl" (Arabic) flips text-anchor's start side and every
    // label extends leftward from its authored x, clipping out of the canvas.
    <svg
      viewBox="0 0 614 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      direction="ltr"
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'Illustration of a custom template editor with Chat and Transcript tabs and a chat message asking for a SOAP note',
        id: 'nOVUlhJ72H',
      })}
      {...props}
    >
      <g clipPath="url(#downgrade-custom-templates-clip0)">
        <rect width="613.33" height="460" fill="#FCFAF8" />
        <rect
          x="46.9357"
          y="33.9357"
          width="512.129"
          height="256.129"
          rx="13.8364"
          fill="white"
          stroke="#ECD7C6"
          strokeWidth="2.12868"
          strokeDasharray="4.26 4.26"
        />
        <path
          d="M103.248 69.7084C102.5 68.4878 100.268 66.6109 99.2578 65.5215C98.6409 64.9965 97.8928 64.6159 97.3809 65.2065C95.2547 67.3459 79.6622 82.9253 78.1003 84.5003C77.6934 84.9597 77.7328 85.0647 77.4047 85.8916C76.9322 87.2041 75.8297 90.1703 75.3441 91.4959C74.9634 92.4015 75.2259 93.3859 76.4072 93.0447C78.1922 92.3622 82.1034 91.1415 83.6916 90.1965C84.4659 89.4353 93.6272 80.2609 99.1659 74.7222L102.565 71.3228C103.143 70.7584 103.484 70.4959 103.287 69.8265L103.235 69.7084H103.248ZM76.8272 91.2465C76.8272 90.564 77.5884 89.0284 77.8641 88.1884C78.1528 87.5453 78.2972 86.7184 78.7566 86.364C79.5966 86.2459 80.2791 87.4665 81.1847 88.1884C82.9697 89.7634 80.7516 90.1309 79.5834 90.5772C78.8616 90.7609 77.3391 91.5878 76.8666 91.3384L76.8272 91.2465ZM82.8122 88.779C82.1691 88.3197 80.5022 86.5872 79.8722 85.9309C79.5966 85.629 79.3341 85.2222 79.5441 84.894C81.6441 82.7547 92.6559 71.7822 96.9478 67.4903C97.4203 67.0309 98.1553 66.0597 98.9166 66.9128C99.6778 67.7265 101.345 69.0259 101.725 69.984C100.82 71.7297 97.6697 74.1184 94.4541 77.6228C90.9497 80.9566 86.1459 86.364 82.9041 88.8053L82.7991 88.779H82.8122Z"
          fill="#28030F"
        />
        <path
          d="M82.8672 93.3794H103.867"
          stroke="#28030F"
          strokeWidth="1.3125"
          strokeLinecap="round"
          strokeLinejoin="bevel"
        />
        <text
          fill="#211217"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-exposure"
          fontSize="38"
          letterSpacing="-0.025em"
        >
          <tspan x="122.404" y="92.1089">
            <FormattedMessage
              defaultMessage="My template"
              id="tDt9YGlYMp"
              description="Title of a mock note template, next to a pencil icon, in the custom-templates illustration on the downgrade modal. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <g filter="url(#downgrade-custom-templates-filter0)">
          <rect
            x="68.2617"
            y="132.011"
            width="330.365"
            height="37.2242"
            rx="9.25446"
            fill="#F6ECE4"
          />
        </g>
        <rect
          x="68.2617"
          y="188.235"
          width="458.323"
          height="37.2242"
          rx="9.25446"
          fill="#F6EFEA"
        />
        <rect
          x="68.2617"
          y="244.459"
          width="468.31"
          height="37.5971"
          rx="9.25446"
          fill="#F6EFEA"
        />
        <g filter="url(#downgrade-custom-templates-filter1)">
          <rect x="99" y="162" width="415" height="261" rx="13.4783" fill="white" />
          <rect
            x="99.4212"
            y="162.421"
            width="414.158"
            height="260.158"
            rx="13.0571"
            stroke="#F0DFD1"
            strokeWidth="0.842391"
          />
          <rect
            x="114.562"
            y="177.563"
            width="67.1176"
            height="40.5588"
            rx="20.2794"
            fill="#28030F"
          />
          <text
            fill="#F9F4F1"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-exposure"
            fontSize="18.8382"
            letterSpacing="-0.02em"
          >
            <tspan x="127.121" y="204.438">
              <FormattedMessage
                defaultMessage="Chat"
                id="qgi5OneiOH"
                description="Label of the selected tab pill, next to the 'Transcript' tab, in the custom-templates illustration on the downgrade modal — the AI chat panel. Limited space — keep the translation as short as the English or it will overflow"
              />
            </tspan>
          </text>
          <rect
            x="185.441"
            y="177.955"
            width="115.333"
            height="39.7739"
            rx="19.8869"
            fill="#28030F"
            fillOpacity="0.01"
          />
          <rect
            x="185.441"
            y="177.955"
            width="115.333"
            height="39.7739"
            rx="19.8869"
            stroke="#28030F"
            strokeWidth="0.784927"
          />
          <text
            fill="#28030F"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-exposure"
            fontSize="18.8382"
            letterSpacing="-0.02em"
          >
            <tspan x="197.607" y="204.438">
              <FormattedMessage
                defaultMessage="Transcript"
                id="7skwpAqOBU"
                description="Label of the unselected tab pill, next to the 'Chat' tab, in the custom-templates illustration on the downgrade modal — the consult transcript panel. Limited space — keep the translation as short as the English or it will overflow"
              />
            </tspan>
          </text>
          <rect
            x="117.727"
            y="255.416"
            width="336.957"
            height="10.1087"
            rx="5.05435"
            fill="url(#downgrade-custom-templates-paint0)"
          />
          <rect
            x="117.727"
            y="271.854"
            width="336.957"
            height="10.1087"
            rx="5.05435"
            fill="url(#downgrade-custom-templates-paint1)"
          />
          <g filter="url(#downgrade-custom-templates-filter2)">
            <path
              d="M114.562 318.443C114.562 310.834 120.731 304.665 128.34 304.665H484.659C492.268 304.665 498.436 310.834 498.436 318.443V393.659C498.436 401.268 492.268 407.437 484.659 407.437H128.34C120.731 407.437 114.562 401.268 114.562 393.659V318.443Z"
              fill="white"
            />
            <path
              d="M128.34 305.086H484.658C492.035 305.086 498.014 311.066 498.015 318.442V393.659C498.015 401.036 492.035 407.016 484.658 407.016H128.34C120.963 407.015 114.983 401.036 114.983 393.659V318.442C114.984 311.066 120.964 305.086 128.34 305.086Z"
              stroke="#F0DFD1"
              strokeWidth="0.842391"
            />
            <text
              fill="#211217"
              style={{ whiteSpace: 'pre' }}
              xmlSpace="preserve"
              className="font-body"
              fontSize="15.163"
              letterSpacing="-0.02em"
            >
              <tspan x="128.041" y="331.788">
                <FormattedMessage
                  defaultMessage="I need a SOAP note for a paediatric nurse"
                  id="6eb0AGIQnQ"
                  description="Message a clinician is typing into a mock chat composer in the custom-templates illustration on the downgrade modal, asking the AI to build a template (a typing cursor is appended after this text). Limited space — keep the translation as short as the English or it will overflow"
                />
                |
              </tspan>
            </text>
            <g clipPath="url(#downgrade-custom-templates-clip1)">
              <g clipPath="url(#downgrade-custom-templates-clip2)">
                <path
                  d="M437.223 381.042V382.165C437.223 383.207 437.637 384.207 438.374 384.945C439.111 385.682 440.111 386.096 441.154 386.096C442.196 386.096 443.196 385.682 443.934 384.945C444.671 384.207 445.085 383.207 445.085 382.165V381.042M441.154 386.096V387.781M441.154 376.549C440.707 376.549 440.278 376.726 439.962 377.042C439.647 377.358 439.469 377.787 439.469 378.234V382.165C439.469 382.612 439.647 383.04 439.962 383.356C440.278 383.672 440.707 383.85 441.154 383.85C441.601 383.85 442.029 383.672 442.345 383.356C442.661 383.04 442.839 382.612 442.839 382.165V378.234C442.839 377.787 442.661 377.358 442.345 377.042C442.029 376.726 441.601 376.549 441.154 376.549Z"
                  stroke="#28030F"
                  strokeWidth="1.01087"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </g>
            <g filter="url(#downgrade-custom-templates-filter3)">
              <path
                d="M461.371 375.538C461.371 372.685 463.684 370.372 466.538 370.372H479.791C482.645 370.372 484.958 372.685 484.958 375.538V388.792C484.958 391.645 482.645 393.959 479.791 393.959H466.538C463.684 393.959 461.371 391.645 461.371 388.792V375.538Z"
                fill="#28030F"
              />
              <g clipPath="url(#downgrade-custom-templates-clip3)">
                <path
                  d="M476.112 382.165L473.163 379.217L470.215 382.165M473.163 379.217V385.114"
                  stroke="#F9F4F1"
                  strokeWidth="0.842391"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
      <defs>
        <filter
          id="downgrade-custom-templates-filter0"
          x="64.2617"
          y="132.011"
          width="338.365"
          height="45.2241"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="downgrade-custom-templates-filter1"
          x="72.0435"
          y="145.152"
          width="468.913"
          height="314.913"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="10.1087" />
          <feGaussianBlur stdDeviation="13.4783" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="downgrade-custom-templates-filter2"
          x="112.035"
          y="302.98"
          width="388.927"
          height="107.826"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.842391" />
          <feGaussianBlur stdDeviation="1.26359" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="0.842391"
            operator="erode"
            in="SourceAlpha"
            result="effect2_dropShadow"
          />
          <feOffset dy="0.842391" />
          <feGaussianBlur stdDeviation="0.842391" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect2_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="downgrade-custom-templates-filter3"
          x="458.844"
          y="368.687"
          width="28.6422"
          height="28.6413"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.842391" />
          <feGaussianBlur stdDeviation="1.26359" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="0.842391"
            operator="erode"
            in="SourceAlpha"
            result="effect2_dropShadow"
          />
          <feOffset dy="0.842391" />
          <feGaussianBlur stdDeviation="0.842391" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect2_dropShadow"
            result="shape"
          />
        </filter>
        <linearGradient
          id="downgrade-custom-templates-paint0"
          x1="117.727"
          y1="260.47"
          x2="454.683"
          y2="260.47"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ECD7C6" />
          <stop offset="1" stopColor="#F4F0F2" />
        </linearGradient>
        <linearGradient
          id="downgrade-custom-templates-paint1"
          x1="117.727"
          y1="276.908"
          x2="454.683"
          y2="276.908"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ECD7C6" />
          <stop offset="1" stopColor="#F4F0F2" />
        </linearGradient>
        <clipPath id="downgrade-custom-templates-clip0">
          <rect width="613.33" height="460" fill="white" />
        </clipPath>
        <clipPath id="downgrade-custom-templates-clip1">
          <path
            d="M434.414 375.463C434.414 375.443 434.431 375.426 434.452 375.426H447.855C447.876 375.426 447.892 375.443 447.892 375.463V388.867C447.892 388.887 447.876 388.904 447.855 388.904H434.452C434.431 388.904 434.414 388.887 434.414 388.867V375.463Z"
            fill="white"
          />
        </clipPath>
        <clipPath id="downgrade-custom-templates-clip2">
          <rect
            width="13.4783"
            height="13.4783"
            fill="white"
            transform="translate(434.414 375.426)"
          />
        </clipPath>
        <clipPath id="downgrade-custom-templates-clip3">
          <path
            d="M468.109 378.833C468.109 377.882 468.88 377.111 469.832 377.111H476.496C477.447 377.111 478.218 377.882 478.218 378.833V385.497C478.218 386.448 477.447 387.22 476.496 387.22H469.832C468.88 387.22 468.109 386.448 468.109 385.497V378.833Z"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
